<template>
	<R3Div class="w-full">
		<div class="flex flex-column p-inputgroup">
			<slot>
				<label v-if="props && props.label" :for="props && props.inputID" class="p-d-block">{{ props.label }}</label>

				<small v-if="props && props.helpText" :for="props && props.inputID">{{ props.helpText }}</small>
				<div :class="['flex flex-row', props.actionLayoutClass]">
					<div
						class="r3-input-container"
						:data-db-table="props.dbTable || undefined"
						:data-db-field="props.dbField || undefined"
						:data-db-obs="props.dbObs || undefined"
						:data-db-label="props.label || undefined"
						:data-db-controle="props.inputID || props.name || undefined"
					>
						<Password v-if="props.password" :id="props && props.inputID" ref="inputRef" v-model="inputText.inputValue"
							:input-props="{ inputmode: keyboardInputMode, autocomplete: autocompleteAttr }" class="w-full" :placeholder="props && props.placeholder"
							:feedback="props.feedBack" :pt="props && props.passthrough"
							:disabled="isDisabled" :readonly="props.readonly"
							@blur="onExit" @keydown="inputKeydownEvent" />

						<InputMask v-if="!props.password && props.mask" :id="props && props.inputID" :name="props.name" ref="inputRef" v-model="inputText.inputValue"
							:mask="props.mask" :inputmode="keyboardInputMode" :autocomplete="autocompleteAttr" aria-autocomplete="none"
							class="w-full" :placeholder="props && props.placeholder" :auto-clear="false"
							:pt="props && props.passthrough" :disabled="isDisabled" :readonly="props.readonly"
							@blur="onExit" @keydown="inputKeydownEvent" />

						<InputText v-if="!props.password && !props.mask" :id="props && props.inputID" :name="props.name" ref="inputRef" v-model="inputText.inputValue"
							:type="nativeInputType" :inputmode="keyboardInputMode" :autocomplete="autocompleteAttr" aria-autocomplete="none" class="w-full" :placeholder="props && props.placeholder"
							:maxlength="props.maxlength != null ? Number(props.maxlength) : undefined"
							:pt="props && props.passthrough" :disabled="isDisabled"
							:readonly="props.readonly" @blur="onExit" @keydown="inputKeydownEvent" />
					</div>

					<Button v-if="props.rightButtonVisible || props.dataFinderRef != null" :icon="`pi ${props.icon || 'pi-search'}`"
						:disabled="isDisabled" @click="rightBtnclick" />


					<!--
						X limpa mesmo com disabledInput: na B321 (e similares) o campo trava
						após selecionar a carga, mas o operador precisa poder apagar e trocar.
						Só desabilita de verdade com `disabled` / enabled=false, ou sem valor.
					-->
					<Button v-if="props.dataFinderRef != null" :class="{ 'ml-1': !props.actionLayoutClass }" icon="pi pi-times"
						:disabled="isClearDisabled" @click="onClearClick" />

					<Button v-if="props.barcodeScanVisible" icon="pi pi-camera" :disabled="isDisabled"
						@click="openScannerDialog" />
				</div>

				<R3BarcodeScanCamera
					v-if="props.barcodeScanVisible"
					v-model:visible="scannerDialogVisible"
					@scan-detected="onScanDetected"
				/>
			</slot>
		</div>
	</R3Div>
</template>

<script lang="ts" setup>
	import R3BarcodeScanCamera from "~/components/R3/R3BarcodeScanCamera.vue";

	/* TODO - Adicionar a possibilidade de colocar um ícone de camera
			ao lado do input para abrir a camera e ler um código de barras */
	import { useDeviceStore } from "~/store/device/deviceStore";
	import type { InputProps } from "~/types/components/R3/input.types";
	// import { R3InputTextClass } from "./classes/R3InputBase.classes";
	import { useExposeProperties } from "#imports";

	import { R3InputTextClass } from "~/classes/base/R3InputBase.classes"
	import { focarProximoCampoEditavel } from "~/utils/focarProximoCampo";



	const inputText = new R3InputTextClass();

	// Computed para o valor de exibição: usa showValue se definido, senão usa inputValue
	/**
	 * Altera o v-model do input para visualização de acordo com o que foi definido, caso foi
	 * preenchido o showValue, ele assume o valor do v-model caso contrário é o valor de inputValue
	 * que assume.
	 */
	// const displayValue = computed({
	// 	get: () => {
	// 		if (inputText.showValue !== undefined && inputText.showValue !== null) {
	// 			return inputText.showValue
	// 		}

	// 		const value = inputText.inputValue;

	// 		if (value == null) {
	// 			return '';
	// 		}

	// 		if (typeof value === 'object') {
	// 			return ''
	// 		}

	// 		return String(value);


	// 	},
	// 	set: (newValue: string) => {
	// 		if (inputText.showValue !== undefined && inputText.showValue !== null) {
	// 			// Se o usuário apagar tudo, limpa também o inputValue e showValue
	// 			if (newValue === '' || newValue === null) {
	// 				inputText.inputValue = null as typeof inputText.inputValue;
	// 				inputText.showValue = undefined;
	// 			}
	// 			// Ignora outras edições quando showValue está definido
	// 			return;
	// 		}



	// 		// Para valores simples (string), atualiza normalmente
	// 		inputText.inputValue = newValue as typeof inputText.inputValue;

	// 	}
	// });

	const props = withDefaults(defineProps<InputProps>(), {
		useBoxShadow: false,
		buttonDataFinderVisible: false,
		barcodeScanVisible: false,
		label: "",
		helpText: "",
		placeholder: "",
		debounce: false,
		debounceTime: 300,
		disabled: false,
		disabledInput: false,
		readonly: false,
		mode: "text",
		/** Desliga Autofill do Android por padrão (tarja preta chave/cartão/pin). */
		autocomplete: "off",
	});

	/** Valor efetivo de autocomplete no input nativo. */
	const autocompleteAttr = computed(() => props.autocomplete || "off");

	const deviceStore = useDeviceStore();

	/**
	 * Desabilitado por prop OU pela classe de estado (`inputText.enabled = false`).
	 * Alinhado ao R3InputNumber.
	 */
	const isDisabled = computed(
		() => Boolean(props.disabled) || Boolean(props.disabledInput) || !inputText.enabled,
	);

	/**
	 * Botão X (limpar): não herda `disabledInput`.
	 * `disabledInput` só trava digitação/lupa; o clear deve continuar liberado
	 * quando há valor (ex.: carga selecionada na B321).
	 */
	const isClearDisabled = computed(
		() => !inputText.inputValue || Boolean(props.disabled) || !inputText.enabled,
	);

	/** inputmode do teclado virtual (coletor pode forçar "none"). */
	const keyboardInputMode = computed(() => {
		if (deviceStore.desativarTeclado) return "none";
		return props.mode || "text";
	});

	/**
	 * Mantém o tipo HTML coerente com o uso do campo. Em especial, type=search
	 * impede que o Android trate a pesquisa do DataFinder como campo genérico
	 * de formulário e mostre atalhos de senha/cartão sobre a lista.
	 */
	const nativeInputType = computed(() => props.mode === "search" ? "search" : "text");


	const emits = defineEmits(
		[
			"update:clear",
			"update:input-value",
			"update:modelValue",
			"keydown:enter",
			"keydown",
			"blur",
			"onRightButtonClick"
		]
	);

	function rightBtnclick() {

		if (!props.onRightButtonClick) {

			if (props.dataFinderRef) {
				props.dataFinderRef.show();
				return;
			}

			// if (inputText.inputValue == null) {
			// 	if (props.dataFinderRef) {
			// 		props.dataFinderRef.show();
			// 	}
			// }
			else {
				(async () => {
					const dataFinder = props.dataFinderRef; // Atribui a uma variável local
					if (dataFinder) { // Verifica a variável local

						dataFinder.search({ sync: true, searchField: dataFinder.keyField, searchValue: inputText.inputValue! }).then((result) => {

							if (result.length > 0) {
								if (dataFinder.onDialogResult) {
									dataFinder.onDialogResult(result[0]);
								};

							}
						});
					}
				}
				)();
			}

		} else {

			props.onRightButtonClick();

		}

	}

	function onClearClick() {
		inputText.inputValue = null;

		// Reseta o confirmedKeyValue para que a próxima seleção da mesma linha
		// no DataFinder continue disparando o watcher (null → valor = change).
		if (props.dataFinderRef) {
			props.dataFinderRef.confirmedKeyValue.value = null;
		}

		emits('update:clear');
		emits('update:input-value', null);
		emits('update:modelValue', null);
	}

	// function rightBtnclick() {
	// 	debugger;
	// 	// DataFinder tem prioridade - verifica primeiro se existe um DataFinder configurado
	// 	if (props.dataFinderRef) {
	// 		// Lógica do DataFinder
	// 		if (inputText.inputValue != null && inputText.inputValue == '')
	// 		{
	// 			props.dataFinderRef.show();
	// 		} else {
	// 			(async () => {
	// 					const dataFinder = props.dataFinderRef; // Atribui a uma variável local
	// 					if (dataFinder) { // Verifica a variável local
	//
	// 						dataFinder.search({ sync: true, searchField: dataFinder.keyField, searchValue: inputText.inputValue! }).then((result) => {
	//
	// 							if (result.length > 0) {
	// 								if (dataFinder.onDialogResult) {
	// 									dataFinder.onDialogResult(result[0]);
	// 								};
	//
	// 							}
	// 						});
	// 					}
	// 				}
	// 			)();
	// 		}
	// 	} else {
	//
	// 		// Se existe listener, apenas emite o evento
	// 		emits('onRightButtonClick');
	//
	// 		// Se não tem nem DataFinder nem evento, não faz nada
	// 	}
	//
	// }

	onBeforeMount(() => {



		//inputNumber = new R3InputNumberClass();

		/*inputNumber.setFocus = () => {
	
			inputRef.value?.$el.querySelector('input').focus();
	
		}*/

		// watch(() => inputNumber.visible, (newValue, oldValue) => {
		// 	console.log('Watcher triggered!');
		// 	console.log('inputNumber object:', inputNumber);
		// 	console.log('inputNumber.visible new value:', newValue);
		// 	console.log('inputNumber.visible old value:', oldValue);
		// })



	})



	// template refs
	const inputRef = ref<HTMLInputElement | null>(null)

	function onExit() {
		/*if (inputNumber.inputValue) {
			props.dataFinderRef.search({
				searchValue: inputNumber.inputValue
			})
		}*/

		emits("blur", inputText.inputValue)
	}

	// function onValueChange(value: number) {
	// 		// if (inputNumber.inputValue !== null) {
	// 		// 	inputNumber.inputValue += value;
	// 		// } else {
	// 		// 	inputNumber.inputValue = value;
	// 		// }
	// 		inputNumber.inputValue! = value
	// 		emits("update:modelValue", inputNumber.inputValue);
	// }

	onMounted(() => {
		// Android Autofill: reforça autocomplete no <input> real (Password/InputText).
		const nativeInput = inputRef.value?.$el?.querySelector?.("input") as HTMLInputElement | null | undefined
			?? (inputRef.value?.$el as HTMLInputElement | undefined);
		if (nativeInput && typeof nativeInput.setAttribute === "function") {
			nativeInput.setAttribute("type", nativeInputType.value);
			nativeInput.setAttribute("autocomplete", autocompleteAttr.value);
			nativeInput.setAttribute("aria-autocomplete", "none");
			nativeInput.setAttribute("autocorrect", "off");
			nativeInput.setAttribute("autocapitalize", "off");
			nativeInput.setAttribute("spellcheck", "false");
		}

		watch(
			() => props.modelValue,
			(val) => {
				if (val !== undefined && val !== inputText.inputValue) {
					inputText.inputValue = val as string;
				}
			},
			{ immediate: true }
		);

		watch(
			() => props.inputValue,
			(val) => {
				if (val !== undefined && val !== inputText.inputValue) {
					inputText.inputValue = val as string;
				}
			},
			{ immediate: true }
		);

		// who emits update events when the internal value changes? previous implementation only
		// emitted on Enter key. this watcher ensures parent v-model bindings stay in sync on every
		// keystroke, blur, programmatic change etc. it prevents the captcha bug described by the
		// user where clicking the confirm button left the model stale.
		watch(
			() => inputText.inputValue,
			(newVal) => {
				// avoid noisy emissions if value hasn't actually changed
				if (newVal !== props.modelValue && newVal !== props.inputValue) {
					emits("update:modelValue", newVal);
					emits("update:input-value", newVal);
				}
			}
		);

		// ── DataFinder auto-sync ──────────────────────────────────────────────
		// Quando o usuário confirma uma seleção no modal do DataFinder, o valor
		// do campo-chave (keyField) é gravado em `confirmedKeyValue` com
		// flush:sync para garantir execução imediata (antes do onDialogResult do
		// parent), permitindo que o parent ainda sobrescreva se precisar exibir
		// um campo diferente (ex.: nome em vez de ID).
		if (props.dataFinderRef) {
			watch(
				() => props.dataFinderRef?.confirmedKeyValue?.value,
				(keyValue) => {
					// null = reset ao abrir o modal ou ao clicar em clear → não limpar o campo
					if (keyValue != null) {
						const strValue = String(keyValue);
						inputText.inputValue = strValue as typeof inputText.inputValue;
						emits("update:modelValue", strValue);
						emits("update:input-value", strValue);
					}
				},
				{ flush: 'sync' }
			);
		}
		// altKey: false, // Se a tecla Alt está pressionada
		// metaKey: false // Se a tecla Meta está pressionada (Command no Mac)
	});

	// Dispara o evento
	// document.dispatchEvent(eventoTecladoTab);
	// console.log("Simulando tecla tab")
	// }

	/**
	 * Avança o foco simulando Tab, replicando o comportamento do coletor Delphi
	 * (Enter confirma o campo e pula para o próximo).
	 *
	 * A função existia apenas no `R3InputNumber`; aqui ela era **chamada mas
	 * nunca definida** — restaram só fragmentos comentados do corpo original
	 * logo acima. O resultado era `ReferenceError: simularTeclaTab is not
	 * defined` a cada Enter, abortando o handler.
	 *
	 * O `emits("keydown:enter")` ocorre na linha anterior à chamada, então o
	 * evento até era emitido; o que quebrava era todo o restante do fluxo —
	 * incluindo o avanço de foco. Em telas operadas por leitor de código de
	 * barras, isso inviabiliza o encadeamento dos campos.
	 */
	/**
	 * Avança o foco para o próximo campo (Enter = Tab do Delphi).
	 * Implementação central em `utils/focarProximoCampo.ts`.
	 */
	function simularTeclaTab() {
		focarProximoCampoEditavel(
			(document.activeElement as HTMLElement | null)
			?? (inputRef.value?.$el as HTMLElement | undefined)
			?? null,
		);
	}

	const { showFieldDbInfo, nomeFormularioAtual } = useFieldDbInfo();

	/** Coletor: Enter pode vir só como keyCode/which 13. */
	function isEnterKey(e: KeyboardEvent): boolean {
		return (
			e.key === "Enter"
			|| e.key === "NumpadEnter"
			|| e.code === "Enter"
			|| e.code === "NumpadEnter"
			|| (e as KeyboardEvent & { keyCode?: number }).keyCode === 13
			|| (e as KeyboardEvent & { which?: number }).which === 13
		);
	}

	function inputKeydownEvent(e: KeyboardEvent) {
		// F10 — origem no banco (LIST_F1), se mapeado
		if (e.key === "F10") {
			if (props.dbTable || props.dbField) {
				e.preventDefault();
				e.stopPropagation();
				showFieldDbInfo({
					tabela: props.dbTable || "—",
					campo: props.dbField || "—",
					formulario: nomeFormularioAtual(),
					controle: props.inputID || props.name || "R3Input",
					label: props.label,
					observacao: props.dbObs,
					valorAtual: inputText.inputValue,
				});
			}
			return;
		}

		if (isEnterKey(e)) {

			if (props.debounce === true) {

				setTimeout(() => {
					emits("keydown:enter", inputText.inputValue);
				}, props.debounceTime);
				return;

			}

			if (props.dataFinderRef) {
				if (!String(inputText.inputValue ?? "").trim()) {
					rightBtnclick();
					return;
				}
			}

			emits("update:modelValue", inputText.inputValue)
			emits("update:input-value", inputText.inputValue)
			emits("keydown:enter", inputText.inputValue);
			// Também repassa o KeyboardEvent p/ handlers legados (`@keydown` com keyCode 13)
			emits("keydown", e);
			simularTeclaTab();
			return;
		}

		// Demais teclas: repassa para a tela (ex.: handlers legados de @keydown)
		emits("keydown", e);
		// if (props.debounce === true) {
		// 	if (e.key === "Enter") {
		// 		setTimeout(() => {
		// 			emits("keydown:enter", inputText.inputValue);
		// 		}, props.debounceTime);
		// 	}
		// }
		// else if (!props.dataFinderRef) {
		//
		// 	if (inputText.inputValue === null) {
		// 		emits("update:modelValue", e.key)
		// 		return;
		// 	}
		// 	emits("update:modelValue", inputText.inputValue)
		// }
		// else {
		// 	if (e.key === "Enter") {
		//
		// 		if (props.dataFinderRef) {
		//
		// 			rightBtnclick();
		// 		}
		// 		else {
		//
		// 			//emits("update:modelValue", inputNumber.inputValue);
		// 			emits("keydown:enter", inputText.inputValue!);
		// 			simularTeclaTab();
		//
		// 		}
		// 	}
		// 	if (e.key === "Tab")
		//
		// 		console.log("Tab pressionado");
		// }
	}

	// Visibilidade de dialogs

	const dataFinderDialogVisible = ref(false);

	function openDataFinderDialog() {
		if (inputText.inputValue == null) { //) {
			//dataFinderDialogVisible.value = true;
			if (props.dataFinderRef) {
				//console.log("dataFinderDialogVisible");
				props.dataFinderRef.show();
			}

		}

	}

	const scannerDialogVisible = ref(false);
	function openScannerDialog() {
		scannerDialogVisible.value = true;
	}

	/**
	 * Código lido pela câmera.
	 *
	 * Emite exatamente o que o Enter emite (ver `inputKeydownEvent`), porque a
	 * tela não deve distinguir a origem: quem escuta `@keydown:enter` já sabe
	 * validar o valor, e duplicar essa lógica por causa da câmera criaria dois
	 * caminhos para o mesmo campo.
	 *
	 * Sem `simularTeclaTab()`: o diálogo acabou de fechar e o foco volta sozinho
	 * ao input; empurrar Tab aqui pularia o campo que o operador acabou de
	 * preencher.
	 */
	function onScanDetected(codigo: string) {
		inputText.inputValue = codigo;

		emits("update:modelValue", codigo);
		emits("update:input-value", codigo);
		emits("keydown:enter", codigo);
	}

	function closeScannerDialog() {
		scannerDialogVisible.value = false;
	}


	// const { visible } = toRefs(inputNumber);

	function setFocus(params?: { limparCampo?: boolean, focarIcone?: boolean }) {
		if (params?.limparCampo) {
			inputText.inputValue = null;
		}
		const el = inputRef.value?.$el as HTMLElement;
		if (el) {
			const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
			input?.focus();
		}
	}
	inputText.setFocus = setFocus;

	type ExposeType = {
		setFocus: typeof setFocus;
	} & typeof inputText;

	const expose = {
		setFocus,
	} as ExposeType

	useExposeProperties(expose, inputText)

	defineExpose(expose);

</script>

<style lang="scss" scoped>
	[v-cloak] {
		display: none
	}

	.p-overlay-open {
		box-shadow: none !important;
	}

	*:focus {
		box-shadow: none !important;
	}

	/* Container do input - transição suave ao redimensionar */
	.r3-input-container {
		flex: 1;
		min-width: 0;
	}
</style>
